/**
 * Auth configuration read from Vite build-time env vars.
 *
 * Provided by .github/workflows/deploy-lambdas.yml, which pulls them from
 * Terraform outputs (cognito_*). For local dev, set VITE_AUTH_ENABLED=false
 * to bypass auth entirely — matches `dev_auth_bypass=true` on the backend.
 *
 * See dashboard/.env.example for the canonical list.
 */

export interface AuthConfig {
  enabled: boolean
  cognitoDomain: string  // e.g. projectbuddy-dev.auth.us-east-1.amazoncognito.com
  clientId: string
  callbackUrl: string    // must exactly match the URL registered in the Cognito User Pool Client
  logoutRedirect: string
  scopes: string         // space-separated OAuth scopes
}

const env = import.meta.env

export const authConfig: AuthConfig = {
  enabled: env.VITE_AUTH_ENABLED === 'true',
  cognitoDomain: env.VITE_COGNITO_DOMAIN ?? '',
  clientId: env.VITE_COGNITO_CLIENT_ID ?? '',
  callbackUrl: env.VITE_AUTH_CALLBACK_URL ?? `${window.location.origin}/auth/callback`,
  logoutRedirect: env.VITE_AUTH_LOGOUT_URL ?? `${window.location.origin}/`,
  scopes: 'openid email profile',
}

/**
 * Throws at startup if auth is enabled but config is incomplete. Better to
 * fail loudly during build/initial render than to silently 401-loop.
 */
export function assertAuthConfigValid(): void {
  if (!authConfig.enabled) return
  const missing: string[] = []
  if (!authConfig.cognitoDomain) missing.push('VITE_COGNITO_DOMAIN')
  if (!authConfig.clientId) missing.push('VITE_COGNITO_CLIENT_ID')
  if (missing.length) {
    throw new Error(
      `Auth is enabled (VITE_AUTH_ENABLED=true) but config is missing: ${missing.join(', ')}. ` +
        `Check the dashboard-spa GitHub Actions step.`,
    )
  }
}
